import React from 'react';
import {
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  useClipboard,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {OverflowMenuVertical} from '@carbon/icons-react';
import {AiFieldDefinition} from '../../../shared/fields/enrichment';
import {useIsFreeOrListBuilderCustomer} from '../../../hooks/api/metadata';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {AgentBuilderModal} from '../../Agents/AgentBuilder/AgentBuilderModal';
import {DeleteFieldModal} from './DeleteFieldModal';
import {useEnrichedFieldCount} from '../../../hooks/api/enrichment';
import {ObjectId} from 'bson';
import {assertNever} from '../../../shared/util';
import {FieldDefinition} from '../../../shared/fields/types';

const getEditStep = (fieldDefinition: AiFieldDefinition) => {
  const {fieldType} = fieldDefinition.config;
  switch (fieldType) {
    case 'boolean':
    case 'category':
    case 'rating':
    case 'estimate':
      return 'editAnalystField';
    case 'legacyResearch':
      return 'editLegacyResearchField';
    case 'research':
      return 'editResearchField';
    case 'legacyTeamSize':
      return 'editLegacyTeamSizeField';
    case 'teamSize':
      return 'editTeamSizeField';
    default:
      assertNever(fieldType);
  }
};

export const CustomFieldMenu = ({
  fieldDefinition,
}: {
  fieldDefinition: FieldDefinition;
}) => {
  const {onCopy} = useClipboard(fieldDefinition.id.toString());
  const toast = useToast();
  const deleteFieldModal = useDisclosure();
  const aiFieldBuilderModal = useDisclosure();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const isFreeOrListBuilder = useIsFreeOrListBuilderCustomer();

  const copyFieldId = () => {
    toast({
      title: 'Field ID copied to clipboard',
      duration: 1_000,
    });
    onCopy();
  };

  if (isFreeOrListBuilder && !isKeyplayAdmin) {
    return null;
  }

  const showDeleteField = isKeyplayAdmin && fieldDefinition.type !== 'ai';

  return (
    <Menu>
      <MenuButton>
        <OverflowMenuVertical />
      </MenuButton>
      <MenuList>
        {fieldDefinition.type === 'ai' && fieldDefinition.agentId && (
          <MenuItem onClick={aiFieldBuilderModal.onOpen}>
            Open Agent Builder
            <AgentBuilderModal
              agentId={fieldDefinition.agentId}
              initialStep={getEditStep(fieldDefinition)}
              isOpen={aiFieldBuilderModal.isOpen}
              onClose={aiFieldBuilderModal.onClose}
            />
          </MenuItem>
        )}
        {showDeleteField && (
          <MenuItem onClick={deleteFieldModal.onOpen} textColor="kred.500">
            Delete Field
            <DeleteFieldModal
              fieldDefinition={fieldDefinition}
              isOpen={deleteFieldModal.isOpen}
              onClose={deleteFieldModal.onClose}
            />
          </MenuItem>
        )}
        {showDeleteField && <Divider />}
        <MenuItem onClick={copyFieldId}>Copy Field ID</MenuItem>
      </MenuList>
    </Menu>
  );
};

export const EnrichedFieldCount = ({
  fieldDefinitionId,
}: {
  fieldDefinitionId: ObjectId;
}) => {
  const {data, isPending} = useEnrichedFieldCount({
    fieldDefinitionId,
  });

  return isPending ? <Skeleton>00</Skeleton> : <>{data?.toLocaleString()}</>;
};
